import { defineStore } from 'pinia'
import _ from 'lodash'
import { BlockBlobClient } from '@azure/storage-blob'


import { resourceApi } from './common/api'
import { rules } from './common/commonFunctions'

const simulationApi = resourceApi.extend('simulationData')
const feedbackApi = resourceApi.extend('feedback')
const blobApi = resourceApi.extend('blob')

export const useFeedback = defineStore('feedback', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['form'],
      storage: localStorage
    }
  ],

  state: () => ({
    form: {
      category: null,
      simulation: null,
      appVersion: null,
      product: null,
      subject: null,
      message: null,
      files: null
    },
    refMessage: null,
    showSimulations: false,
    showAppVersion: false,
    showProducts: false,
    rules: rules.feedback,
    categories: [
      'Technical Issue / Bug',
      'Request for New Simulation',
      'Platform Feature Request',
      'General Feedback'
    ],
    products: ['Portal', 'VR App'],
    simulations: null,
    isSubmitMessage: false,
    isLoadingSubmit: false,
    errorSubmit: null,
    statusSubmit: null,
    isLoadingSimulations: false,
    errorSimulations: null,
    statusSimulations: null,
    fetchFrequency: 300000, // 5 minutes
    fetchSimulationsInterval: null,
    uploadProgress: null
  }),

  getters: {
    getRules: ({ rules }) => {
      return rules
    },
    getCategories: ({ categories }) => {
      return categories
    },
    getProducts: ({ products }) => {
      return products
    },
    getSimulations: ({ simulations }) => {
      return simulations
    },
    getUploadProgress({ uploadProgress }) {
      return uploadProgress
    },
    getIsLoadingSubmit: ({ isLoadingSubmit }) => {
      return isLoadingSubmit
    },
    getIsLoadingSimulations: ({ isLoadingSimulations }) => {
      return isLoadingSimulations
    },
    getFetchFrequency: ({ fetchFrequency }) => {
      return fetchFrequency
    },
    getFetchSimulationsInterval: ({ fetchSimulationsInterval }) => {
      return fetchSimulationsInterval
    },
    getIsLoadingMessage: ({ isSubmitMessage }) => {
      if (isSubmitMessage)
        return 'Your feedback has been submitted successfully.'
    },
    getErrors: ({ errorSubmit, errorSimulations }) => {
      let returnValue = []
      if (errorSubmit)
        returnValue.push({
          text: errorSubmit,
          key: 'submit'
        })
      if (errorSimulations)
        returnValue.push({
          text: errorSimulations,
          key: 'simulations'
        })
      return returnValue
    }
  },

  actions: {
    async fetchSimulations(withLoader = true) {
      if (this.errorSimulations != null) this.errorSimulations = null
      this.statusSimulations = null
      if (withLoader) this.isLoadingSimulations = true
      try {
        const result = await simulationApi.get('', {
          params: { active: 1, distinct: 'title' }
        })
        const { error, simulationData } = result.data.response
        this.statusSimulations = result.data.status
        if (error) this.errorSimulations = error.message
        else {
          this.simulations = simulationData
        }
      } catch (e) {
        this.errorSimulations =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingSimulations = false
    },
    resetForm() {
      const emptyState = {
        category: null,
        simulation: null,
        appVersion: null,
        product: null,
        subject: null,
        message: null,
        files: null
      }
      _.assign(this.form, emptyState)
      if (this.refMessage != null) {
        this.refMessage.resetContent(null)
        this.refMessage = null
      }
      this.showSimulations = false
      this.showAppVersion = false
      this.showProducts = false
    },
    async submitForm() {
      const getSubmissionId = () => {
        const ts = Date.now()
        const rnd = crypto.randomUUID().slice(0, 8)
        return `${rnd}_${ts}`
      }
      this.isSubmitMessage = false
      if (this.errorSubmit != null) this.errorSubmit = null
      this.statusSubmit = null
      this.isLoadingSubmit = true
      try {
        this.form.submissionId = getSubmissionId()
        if (this.form.files != null && this.form.files.length) {
          const payload = {
            container: 'feedback',
            path: this.form.submissionId,
            duration: 5,
            unit: 'minute',
            files: _.reduce(
              this.form.files,
              (a, { name }, i) => {
                a[i] = name
                return a
              },
              {}
            )
          }
          const result = await blobApi.put('', payload)
          const { error, blob } = result.data.response
          this.statusSubmitMessage = result.data.status
          if (error) this.errorSubmitMessage = error.message
          else {
            const totalSize = this.form.files.reduce(
              (sum, f) => sum + f.size,
              0
            )
            this.uploadProgress = 0
            let totalLoaded = 0
            const uploadFiles = await Promise.all(
              this.form.files.map(async (file, index) => {
                const f = blob[index]
                if (file && _.has(f, 'url') && !_.isEmpty(f.url)) {
                  const blobClient = new BlockBlobClient(f.url)
                  try {
                    await blobClient.uploadData(file, {
                      blobHTTPHeaders: {
                        blobContentType: file.type || 'application/octet-stream'
                      },
                      onProgress: (event) => {
                        totalLoaded += event.loadedBytes
                        this.uploadProgress = Math.min(
                          Number(((totalLoaded / totalSize) * 100).toFixed(2)),
                          100
                        )
                      }
                    })
                    return { index, status: 'success' }
                  } catch (err) {
                    return { index, status: 'failed', error: err.message }
                  }
                }
              })
            )
            const errors = uploadFiles.filter((r) => r.status == 'failed')
            this.uploadProgress = null
            if (errors.length > 0)
              this.errorSubmitMessage.value = `Upload failed for the following file(s): ${errors.map(({ index, error }) => `File '${this.form.files[parseInt(index)].name}' (${error})`).join(', ')}`
            else {
              const payload = {
                container: 'feedback',
                path: this.form.submissionId,
                duration: 100,
                unit: 'year',
                files: _.reduce(
                  this.form.files,
                  (a, { name }, i) => {
                    a[i] = name
                    return a
                  },
                  {}
                )
              }
              const result = await blobApi.post('', payload)
              const { error, blob } = result.data.response
              this.statusSubmitMessage = result.data.status
              if (error) this.errorSubmitMessage = error.message
              else {
                this.form.files = _.values(blob)
              }
            }
          }
        }
        const result = await feedbackApi.post(
          '',
          _.pickBy(this.form, (v) => v != null)
        )
        const { error, feedback } = result.data.response
        this.statusSubmit = result.data.status
        if (error) this.errorSubmit = error.message
        else {
          this.isSubmitMessage = feedback
        }
      } catch (e) {
        this.errorSubmit =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingSubmit = false
      this.resetForm()
    },
    setFetchSimulationsInterval(newValue) {
      if (newValue == null && this.fetchSimulationsInterval != null)
        clearInterval(this.fetchSimulationsInterval)
      this.fetchSimulationsInterval = newValue
    },
    cancelError(key) {
      switch (key) {
        case 'simulations':
          this.errorSimulations = null
          break
        case 'submit':
          this.errorSubmit = null
          break
        default:
          break
      }
    }
  }
})
